'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherFileText, FeatherUpload } from '@subframe/core';
import { useEffect, useRef, useState } from 'react';
import { Button, CurrencyInput, Dialog, TextArea, TextField } from '@/shared/components/ui';
import type { Fatura } from '../types';
import { dataAtualISO, formatarMoedaBR } from '../utils/faturaFormatters';
const MAX_FILE_SIZE_MB = 5;
interface AnexarComprovanteModalProps {
  isOpen: boolean;
  fatura: Fatura | null;
  saving: boolean;
  onClose: () => void;
  onConfirm: (data: {
    arquivoNome: string;
    arquivoTipo: 'IMAGEM' | 'PDF';
    dataPagamento: string;
    valorConfirmado: number;
    observacao: string | null;
    file: File;
  }) => void;
}
export function AnexarComprovanteModal(t0) {
  const $ = _c(72);
  const {
    isOpen,
    fatura,
    saving,
    onClose,
    onConfirm
  } = t0;
  const [arquivo, setArquivo] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = dataAtualISO();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [dataPagamento, setDataPagamento] = useState(t1);
  const [valorConfirmado, setValorConfirmado] = useState(0);
  const [observacao, setObservacao] = useState("");
  const [erroArquivo, setErroArquivo] = useState(null);
  const inputRef = useRef(null);
  let t2;
  let t3;
  if ($[1] !== fatura || $[2] !== isOpen) {
    t2 = () => {
      if (isOpen && fatura) {
        setArquivo(null);
        setDataPagamento(dataAtualISO());
        setValorConfirmado(fatura.valorTotal);
        setObservacao("");
        setErroArquivo(null);
      }
    };
    t3 = [isOpen, fatura];
    $[1] = fatura;
    $[2] = isOpen;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  if (!fatura) {
    return null;
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setErroArquivo(`Arquivo acima de ${MAX_FILE_SIZE_MB}MB`);
        setArquivo(null);
        return;
      }
      const tiposPermitidos = ["image/jpeg", "image/png", "application/pdf"];
      if (!tiposPermitidos.includes(file.type)) {
        setErroArquivo("Formato inv\xE1lido \u2014 use JPG, PNG ou PDF");
        setArquivo(null);
        return;
      }
      setErroArquivo(null);
      setArquivo(file);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleFileChange = t4;
  const arquivoOk = arquivo !== null && erroArquivo === null;
  const dataValida = dataPagamento && dataPagamento <= dataAtualISO();
  const valorValido = valorConfirmado > 0;
  const podeConfirmar = arquivoOk && dataValida && valorValido && !saving;
  let t5;
  if ($[6] !== arquivo || $[7] !== dataPagamento || $[8] !== observacao || $[9] !== onConfirm || $[10] !== podeConfirmar || $[11] !== valorConfirmado) {
    t5 = () => {
      if (!arquivo || !podeConfirmar) {
        return;
      }
      onConfirm({
        arquivoNome: arquivo.name,
        arquivoTipo: arquivo.type === "application/pdf" ? "PDF" : "IMAGEM",
        dataPagamento: new Date(`${dataPagamento}T12:00:00`).toISOString(),
        valorConfirmado,
        observacao: observacao.trim() || null,
        file: arquivo
      });
    };
    $[6] = arquivo;
    $[7] = dataPagamento;
    $[8] = observacao;
    $[9] = onConfirm;
    $[10] = podeConfirmar;
    $[11] = valorConfirmado;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleConfirmar = t5;
  const valorDiferente = valorConfirmado !== fatura.valorTotal;
  let t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="bg-success-50 flex h-10 w-10 shrink-0 items-center justify-center rounded-full"><FeatherUpload className="text-success-600 h-5 w-5" /></div>;
    t7 = <h2 className="text-body-bold font-body-bold text-default-font">Anexar comprovante de pagamento</h2>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  const t8 = fatura.tenantNome;
  const t9 = fatura.numero;
  let t10;
  if ($[15] !== fatura.valorTotal) {
    t10 = formatarMoedaBR(fatura.valorTotal);
    $[15] = fatura.valorTotal;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== fatura.numero || $[18] !== fatura.tenantNome || $[19] !== t10) {
    t11 = <div className="flex items-start gap-3">{t6}<div>{t7}<p className="text-caption font-caption text-subtext-color mt-1">{t8} · {t9} · {t10}</p></div></div>;
    $[17] = fatura.numero;
    $[18] = fatura.tenantNome;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex items-start gap-2 rounded-[8px] bg-neutral-50 px-3 py-2 text-sm text-neutral-700">Ao confirmar, a fatura será marcada como paga manualmente e a cobrança no Asaas será cancelada para evitar cobrança duplicada.</div>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <label className="text-sm font-medium text-neutral-700">Comprovante *</label>;
    t14 = <input ref={inputRef} type="file" accept="image/jpeg,image/png,application/pdf" onChange={handleFileChange} className="hidden" />;
    $[22] = t13;
    $[23] = t14;
  } else {
    t13 = $[22];
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => inputRef.current?.click();
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <FeatherFileText className="text-brand-600 h-5 w-5 shrink-0" />;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const t17 = arquivo ? arquivo.name : "Selecionar arquivo";
  let t18;
  if ($[26] !== t17) {
    t18 = <span className="text-body font-body text-default-font">{t17}</span>;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="text-caption font-caption text-subtext-color">JPG, PNG ou PDF — até {MAX_FILE_SIZE_MB}MB</span>;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== t18) {
    t20 = <button type="button" onClick={t15} className="bg-default-background hover:border-brand-400 flex items-center gap-3 rounded-[8px] border border-dashed border-neutral-300 px-4 py-3 text-left transition-colors">{t16}<div className="flex flex-col gap-0.5">{t18}{t19}</div></button>;
    $[29] = t18;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== erroArquivo) {
    t21 = erroArquivo && <p className="text-error-700 text-caption font-caption">{erroArquivo}</p>;
    $[31] = erroArquivo;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t20 || $[34] !== t21) {
    t22 = <div className="flex flex-col gap-2">{t13}{t14}{t20}{t21}</div>;
    $[33] = t20;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  let t24;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = dataAtualISO();
    t24 = e => setDataPagamento(e.target.value);
    $[36] = t23;
    $[37] = t24;
  } else {
    t23 = $[36];
    t24 = $[37];
  }
  let t25;
  if ($[38] !== dataPagamento) {
    t25 = <TextField label="Data do pagamento *"><TextField.Input type="date" value={dataPagamento} max={t23} onChange={t24} /></TextField>;
    $[38] = dataPagamento;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = val => setValorConfirmado(val);
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  const t27 = valorDiferente ? "Pagamento parcial \u2014 aten\xE7\xE3o" : undefined;
  let t28;
  if ($[41] !== t27 || $[42] !== valorConfirmado) {
    t28 = <CurrencyInput label="Valor confirmado *" value={valorConfirmado} onChange={t26} helpText={t27} />;
    $[41] = t27;
    $[42] = valorConfirmado;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] !== t25 || $[45] !== t28) {
    t29 = <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{t25}{t28}</div>;
    $[44] = t25;
    $[45] = t28;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <label className="text-sm font-medium text-neutral-700">Observação <span className="text-subtext-color font-normal">(opcional)</span></label>;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = e_0 => setObservacao(e_0.target.value);
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  if ($[49] !== observacao) {
    t32 = <div className="flex flex-col gap-1.5">{t30}<TextArea><TextArea.Input placeholder="Ex: TED recebida na conta principal, confirmada no extrato" value={observacao} onChange={t31} rows={2} /></TextArea></div>;
    $[49] = observacao;
    $[50] = t32;
  } else {
    t32 = $[50];
  }
  let t33;
  if ($[51] !== onClose || $[52] !== saving) {
    t33 = <Button variant="neutral-secondary" size="medium" onClick={onClose} disabled={saving}>Cancelar</Button>;
    $[51] = onClose;
    $[52] = saving;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <FeatherUpload />;
    $[54] = t34;
  } else {
    t34 = $[54];
  }
  const t35 = !podeConfirmar;
  let t36;
  if ($[55] !== handleConfirmar || $[56] !== saving || $[57] !== t35) {
    t36 = <Button variant="brand-primary" size="medium" icon={t34} onClick={handleConfirmar} loading={saving} disabled={t35}>Marcar como paga</Button>;
    $[55] = handleConfirmar;
    $[56] = saving;
    $[57] = t35;
    $[58] = t36;
  } else {
    t36 = $[58];
  }
  let t37;
  if ($[59] !== t33 || $[60] !== t36) {
    t37 = <div className="flex items-center justify-end gap-2 border-t border-neutral-100 pt-4">{t33}{t36}</div>;
    $[59] = t33;
    $[60] = t36;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  let t38;
  if ($[62] !== t11 || $[63] !== t22 || $[64] !== t29 || $[65] !== t32 || $[66] !== t37) {
    t38 = <Dialog.Content className="max-w-lg"><div className="flex w-full flex-col gap-5 p-6">{t11}{t12}{t22}{t29}{t32}{t37}</div></Dialog.Content>;
    $[62] = t11;
    $[63] = t22;
    $[64] = t29;
    $[65] = t32;
    $[66] = t37;
    $[67] = t38;
  } else {
    t38 = $[67];
  }
  let t39;
  if ($[68] !== isOpen || $[69] !== onClose || $[70] !== t38) {
    t39 = <Dialog open={isOpen} onOpenChange={onClose}>{t38}</Dialog>;
    $[68] = isOpen;
    $[69] = onClose;
    $[70] = t38;
    $[71] = t39;
  } else {
    t39 = $[71];
  }
  return t39;
}